import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  Inject,
  forwardRef
} from "@angular/core";

//Services
import { QuestionService } from "app/core/services/question.service";
import { DimmerService } from "app/core/services/dimmer.service";
import { AlertService } from "app/core/services/alert.service";

//Models
import { CurriculumReview } from "app/core/models/curriculum-review";
import { Question } from "app/core/models/question";
import { QuestionAnswerOption } from "app/core/models/question-answer-option";
import { QuestionTextAnswer } from "app/core/models/question-text-answer";
import { QuestionSelectedOption } from "app/core/models/question-selected-option";
import { CustomError } from "app/core/models/custom-error";

//Libraries
import { finalize } from "rxjs/operators";

@Component({
  selector: "app-user-step-course-question",
  templateUrl: "./user-step-course-question.component.html",
  styleUrls: ["./user-step-course-question.component.scss"]
})
export class UserStepCourseQuestionComponent implements OnInit {
  @Input()
  courseId: number;
  @Input()
  review: CurriculumReview;
  @Output()
  disableNavigation: EventEmitter<boolean> = new EventEmitter();

  // @Output() nextStep: EventEmitter<string> = new EventEmitter();
  // @Output() prevStep: EventEmitter<string> = new EventEmitter();

  //data
  questions: Array<Question> = [];
  questionForForm: Question;
  questionsForForm: Array<Question> = [];
  // textAnswerForForm: QuestionTextAnswer;
  answerOptionsForForm: Array<QuestionAnswerOption> = [];
  selectedRadioOptionId: number;
  relatingTo = "course";

  editable: boolean = true; //quetion list component is editable

  //flags
  loading: { isLoading: boolean; message: string };
  saving: boolean = false;
  shouldShowAnswerForm: boolean = false;
  answerFormType: string = null;
  isValueChanged: boolean = false;
  justSaved: boolean = false;

  //errors
  loadError: CustomError;
  actionErrors: Array<CustomError> = [];

  loadErrorTitle: string =
    "There were errors while loading course outcomes information.";
  actionErrorTitle: string = "There were errors while processing the request.";

  constructor(
    private questionService: QuestionService,
    private dimmerService: DimmerService,
    private alertService: AlertService
  ) {}

  getQuestions(courseId: number, relatingTo: string) {
    return this.questionService
      .getQuestionsByCourseId(courseId, relatingTo)
      .then(
        (questions: Array<Question>) => questions,
        (error: CustomError) => {
          throw error;
        }
      );
  }

  getTextAnswerForCourse(courseId: number, questionId: number) {
    return this.questionService
      .getTextAnswerForCourse(courseId, questionId)
      .then(
        (textAnswer: QuestionTextAnswer) => textAnswer,
        error => {
          throw error;
        }
      );
  }

  getAnswerOptions(courseId: number, questionId: number) {
    return this.questionService
      .getAnswerOptionsByCourseId(courseId, questionId)
      .then(
        (answerOptions: Array<QuestionAnswerOption>) => answerOptions,
        error => {
          throw error;
        }
      );
  }

  getSelectedAnswerOptionsForCourse(courseId: number, questionId: number) {
    return this.questionService
      .getSelectedAnswerOptionsForCourseByCourseId(courseId, questionId)
      .then(
        (selectedAnswerOptions: Array<QuestionSelectedOption>) => {
          // console.log(selectedAnswerOptions);
          return selectedAnswerOptions;
        },
        error => {
          throw error;
        }
      );
  }

  async ngOnInit() {
    this.loading = {
      isLoading: true,
      message: "Loading additional questions..."
    };

    try {
      let questions = await this.getQuestions(this.courseId, "course");

      for (const question of questions) {
        if (question.format == "text") {
          question.textAnswer = await this.getTextAnswerForCourse(
            this.courseId,
            question.id
          );

          //console.log(question.textAnswer);
        } else if (
          question.format == "checkbox" ||
          question.format == "radio"
        ) {
          question.options = await this.getAnswerOptions(
            this.courseId,
            question.id
          );

          let selectedOptions: Array<QuestionSelectedOption> = [];

          selectedOptions = await this.getSelectedAnswerOptionsForCourse(
            this.courseId,
            question.id
          );

          //convert to array
          let selectedOptionsArray: Array<QuestionSelectedOption> = [];

          selectedOptions.forEach(selectedOption => {
            //console.log(selectedOption);

            selectedOptionsArray[
              selectedOption.selectedOptionId
            ] = selectedOption;
          });

          //console.log(selectedOptionsArray);

          //set isSelected
          question.options.forEach(option => {
            if (selectedOptionsArray[option.id]) {
              option.isSelected = true;
              option.otherText =
                selectedOptionsArray[option.id].selectedOptionText;
            } else {
              option.isSelected = false;
              option.otherText = "";
            }
          });
        }
      }

      this.questions = questions;
    } catch (error) {
      console.log(error);

      this.loadError = error;

    } finally {
      this.loading.isLoading = false;
    }
  }

  async saveAnswers(questions: Array<Question>) {
    this.saving = true;

    let errors: Array<CustomError> = [];

    try {
      for (let question of questions) {
        if (question.format == "text") {
          if (question.textAnswer.id) {
            //update

            //console.log("Updating");

            await this.questionService.updateTextAnswerForCourse(
              this.courseId,
              question.id,
              question.textAnswer
            );
          } else {
            //create
            //console.log("Creating");

            await this.questionService.addTextAnswerForCourse(
              this.courseId,
              question.id,
              question.textAnswer
            );
          }
        }

        if (question.format == "checkbox" || question.format == "radio") {
          //console.log(question);

          await this.questionService.postSelectedAnswerOptionsForCourse(
            this.courseId,
            question.id,
            question.options
          );
        }
      }

      this.justSaved = true;

      setTimeout(() => {
        this.justSaved = false;
      }, 2000);


    } catch (e) {
      console.log(e);
      this.actionErrors.push(e);
    } finally {
      //save completed
      this.saving = false;
      this.isValueChanged = false;
      this.emitDisableNavigation(false);
    }
  }

  /********************************
   * Triggered by child component
   *********************************/
  updateQuestions(questions: Array<Question>) {
    this.questions = questions;
  }

  valueChanged() {
    this.isValueChanged = true;
    this.emitDisableNavigation(true);
  }

  radioSelectionChanged(question, optionId) {
    this.isValueChanged = true;

    question.options.forEach(option => {
      if (option.id == optionId) {
        option.isSelected = true;
      } else {
        option.isSelected = false;
      }
    });
  }

  cancelForm() {
    this.dimmerService.dimWindow(false);
    this.shouldShowAnswerForm = false;
    this.questionForForm = null;
  }

  // /**********************************
  //  * Next step clicked
  //  *********************************/
  // nextStepClicked() {
  //   this.nextStep.emit();
  // }

  // /**********************************
  //  * Previous step clicked
  //  *********************************/
  // previousStepClicked() {
  //   this.prevStep.emit();
  // }

  clearAll() {
    this.dimmerService.dimWindow(false);
    this.shouldShowAnswerForm = false;
    this.questionForForm = null;
  }

  handleActionErrors() {
    if (this.actionErrors.length > 0) {
      //show alert - warning
      this.alertService.showAlert(
        false,
        "Error while updating the database.",
        false,
        this.actionErrors
      );
    }
  }

  emitDisableNavigation(isDisabled: boolean) {
    this.disableNavigation.emit(isDisabled);
  }
}
